/**
 * Accumulates the burst of `seek` events a progress-bar drag produces (one
 * JW `seek` per `mousemove`/`touchmove`) into a single gesture, so callers
 * can emit one Pulse `Seek` per drag instead of one per `seek`.
 *
 * The gesture's start/end boundaries are supplied by the caller - in
 * practice, the core's `scrubStart`/`scrubEnd` events, themselves driven by
 * the skin's pointer handling. This class deliberately knows nothing about
 * the player or the DOM: it is a dependency-free accumulator that reacts
 * only to `begin()`/`push()`/`flush()` calls made on it.
 */
export type SeekGesturePoint = {
    position: number;
    offset: number;
};

export type SeekGestureFlush = {
    seekFromMs: number;
    seekToMs: number;
};

type Burst = {
    first: SeekGesturePoint;
    last: SeekGesturePoint;
};

export class SeekGesture {
    private readonly onFlush: (payload: SeekGestureFlush) => void;
    private burst: Burst | null = null;
    private destroyed = false;

    constructor(onFlush: (payload: SeekGestureFlush) => void) {
        this.onFlush = onFlush;
    }

    /**
     * Starts a gesture, discarding any stale burst left behind by a
     * previous gesture that was never flushed.
     */
    begin() {
        this.burst = null;
    }

    push(point: SeekGesturePoint) {
        if (this.destroyed) {
            return;
        }

        this.burst = this.burst
            ? { first: this.burst.first, last: point }
            : { first: point, last: point };
    }

    /**
     * Emits the accumulated burst once and clears it. A no-op when the
     * burst is empty, so it is safe to call repeatedly.
     */
    flush() {
        if (!this.burst) {
            return;
        }

        const { first, last } = this.burst;
        this.burst = null;

        this.onFlush({
            seekFromMs: Math.floor(first.position * 1000),
            seekToMs: Math.floor(last.offset * 1000),
        });
    }

    destroy() {
        this.destroyed = true;
        this.burst = null;
    }
}
